import React, { createContext, ReactElement, ReactNode, useContext, useMemo } from "react";

import { Cell, Row } from "@tanstack/react-table";

import { DatatableRowData, DatatableRowDataWithCacheKey } from "./datatable-types";

export const generateCellCacheKey = <TData extends DatatableRowData>(
  row: Row<DatatableRowDataWithCacheKey<TData>>,
  cell: Cell<DatatableRowDataWithCacheKey<TData>, unknown>,
) => {
  return `${row.original.__cacheKey}-${cell.column.id}`;
};

// Define the type for the cache
type CellCache = Map<string, ReactElement | JSX.Element>;

const GLOBAL_CELL_CACHE: Map<string, CellCache> = new Map();

// Define the context value type
interface DatatableCellCacheContextType {
  getCellFromCache: (id: string) => ReactElement | JSX.Element | undefined;
  writeCellToCache: (id: string, row: ReactElement | JSX.Element) => void;
  resetCache: (shouldReset?: (key: string) => boolean) => void;
}

// Create the context
const DatatableCellCacheContext = createContext<DatatableCellCacheContextType | undefined>(undefined);

// Provider component
interface DatatableCellCacheProviderProps {
  children: ReactNode;
  viewKey: string;
}

export const DatatableCellCacheProvider: React.FC<DatatableCellCacheProviderProps> = ({ children, viewKey }) => {
  // Ensure a cache exists for this viewKey in the global singleton
  const cache = useMemo(() => {
    if (!GLOBAL_CELL_CACHE.has(viewKey)) {
      GLOBAL_CELL_CACHE.set(viewKey, new Map());
    }
    return GLOBAL_CELL_CACHE.get(viewKey)!;
  }, [viewKey]);

  const getCellFromCache = (id: string) => {
    return cache.get(id);
  };

  const writeCellToCache = (id: string, row: ReactElement | JSX.Element) => {
    cache.set(id, row);
  };

  const resetCache = (shouldReset?: (key: string) => boolean) => {
    if (!shouldReset) {
      cache.clear();
      return;
    }
    for (const key of Array.from(cache.keys())) {
      if (shouldReset(key)) {
        cache.delete(key);
      }
    }
  };

  const contextValue = useMemo(
    () => ({
      getCellFromCache,
      writeCellToCache,
      resetCache,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cache],
  );

  return <DatatableCellCacheContext.Provider value={contextValue}>{children}</DatatableCellCacheContext.Provider>;
};

// Hook to use the DatatableCellCache context
export const useDatatableCellCache = (): DatatableCellCacheContextType => {
  const context = useContext(DatatableCellCacheContext);
  if (!context) {
    throw new Error("useDatatableCellCache must be used within a DatatableCellCacheProvider");
  }
  return context;
};
